import { diffPendingSettings } from 'components/agent-chat/pending-settings';
import { zip, groupBy } from 'lodash-es';
import type {
  AggregatedFreeCashFlow,
  FreeCashFlowAggregationPeriod,
  FreeCashFlowEntry,
  ProductionType,
  ProductionVolumeEntry,
  SlotCategory,
  SlotVolumeSource,
  SlotAfeSource,
} from 'types/evaluation';

import type {
  AggregatedFreeCashFlowResultResponse,
  AfeSummariesResponse,
} from 'types/api-responses';
import httpRequest from 'lib/request';
import type {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
} from '../evaluation-result/aggregated-free-cash-flow/types';
import type {
  CalculateRequest,
  CalculationDetail,
  EvaluationWorkspace,
  SlotDateOverride,
} from './types';

// ---------------------------------------------------------------------------
// WorkingCopyValues — the subset of CalculationDetail that hydrates atoms.
// ---------------------------------------------------------------------------

/**
 * Values extracted from a CalculationDetail to hydrate working copy atoms
 * on tile click. Only includes the fields actually sent to /evaluations/calculate;
 * all other settings (aggregation, measurementType, slotVolumeSource, date range
 * filters) reset to defaults on tile click.
 *
 * @property {string | null} effectiveDate - The effective date from the detail config.
 * @property {Record<string, SlotVolumeSourceConfiguration>} slotVolumeConfigurations - Per-slot volume source overrides keyed by slot ID.
 * @property {number} eval2Id - The Eval 2.0 run ID from the detail.
 */
export interface WorkingCopyValues {
  evalMode: 'case' | 'legacy';
  evaluationCaseId: number | null;
  effectiveDate: string | null;
  slotVolumeSource: SlotVolumeSource;
  slotAfeSource: SlotAfeSource;
  priceDeckId: number | null;
  dcaScenarioId: number | null;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  slotDateConfigurations?: Record<string, SlotDateOverride>;
  // Scenario id from `detail.scenario.id`. Always present on detail per
  // INS-8827 (the BE pins scenario_id on the EvaluationCalculation).
  scenarioId: number | null;
  // Legacy eval2 run id from `detail.eval2EvaluationId`. Null on calcs
  // created post-INS-8827; populated for legacy rows so the eval2 link
  // header can still render.
  eval2Id: number | null;
  dpsId?: number | null;
  drsId?: number | null;
  differentialScenarioId?: number | null;
  taxScenarioId?: number | null;
  expenseScenarioId?: number | null;
}

// ---------------------------------------------------------------------------
// extractWorkingCopyFromDetail
// ---------------------------------------------------------------------------

/**
 * Extracts the working copy values from a CalculationDetail response.
 * Maps snake_case API fields to camelCase atoms shape.
 *
 * @param {CalculationDetail} detail - The full calculation detail from the API.
 * @return {WorkingCopyValues} The values to hydrate working copy atoms.
 */
export const extractWorkingCopyFromDetail = (
  detail: CalculationDetail
): WorkingCopyValues => {
  const slotVolumeConfigurations: Record<
    string,
    SlotVolumeSourceConfiguration
  > = {};

  if (detail.configurations.slot_volume_sources) {
    for (const src of detail.configurations.slot_volume_sources) {
      slotVolumeConfigurations[src.slot_id] = {
        volumeSourceId: src.volume_source_id,
        volumeSourceType: src.volume_source_type,
        ...(src.forecast_source ? { forecastSource: src.forecast_source } : {}),
        ...(src.dca_scenario ? { dcaScenario: src.dca_scenario } : {}),
      };
    }
  }

  // Parse slot_afe_sources into separate AFE and cost date configs
  const slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration> = {};
  const slotCostDateConfigurations: Record<string, SlotCostDateConfiguration> =
    {};

  if (detail.configurations.slot_afe_sources) {
    for (const src of detail.configurations.slot_afe_sources) {
      // An entry may contain only cost-date overrides without an AFE selection
      if (src.afe_header_id) {
        slotAfeConfigurations[src.slot_id] = {
          afeSourceId: src.afe_header_id,
        };
      }

      const costDates: SlotCostDateConfiguration = {};
      if (src.drilling_costs_date)
        costDates.drillingCostsDate = src.drilling_costs_date;
      if (src.completion_costs_date)
        costDates.completionCostsDate = src.completion_costs_date;
      if (src.facilities_costs_date)
        costDates.facilitiesCostsDate = src.facilities_costs_date;
      if (src.contingencies_costs_date)
        costDates.contingenciesCostsDate = src.contingencies_costs_date;
      if (src.other_costs_date) costDates.otherCostsDate = src.other_costs_date;

      if (Object.keys(costDates).length > 0) {
        slotCostDateConfigurations[src.slot_id] = costDates;
      }
    }
  }

  // Parse slot_date_overrides into SlotDateOverride configs
  const slotDateConfigurations: Record<string, SlotDateOverride> = {};
  if (detail.configurations.slot_date_overrides) {
    for (const src of detail.configurations.slot_date_overrides) {
      const override: SlotDateOverride = {};
      if (src.permit_date !== undefined) override.permitDate = src.permit_date;
      if (src.spud_date !== undefined) override.spudDate = src.spud_date;
      if (src.first_production_date !== undefined)
        override.firstProductionDate = src.first_production_date;
      if (src.cashflow_date !== undefined)
        override.cashflowDate = src.cashflow_date;

      if (Object.keys(override).length > 0) {
        slotDateConfigurations[src.slot_id] = override;
      }
    }
  }

  return {
    evalMode: detail.configurations.evaluation_case_id
      ? ('case' as const)
      : ('legacy' as const),
    evaluationCaseId: detail.configurations.evaluation_case_id
      ? Number(detail.configurations.evaluation_case_id)
      : null,
    effectiveDate: detail.configurations.effective_date || null,
    // INS-9653: a stale eval_2_0 on a calc with no eval2 link (DPS-only calcs have
    // eval2EvaluationId === null by design) must not be carried into the working
    // copy — otherwise the next Calculate re-sends it (buildCalculateRequest always
    // emits slot_volume_source) and re-persists the broken state. Fall back to
    // eval_3_0 in that case.
    slotVolumeSource:
      detail.configurations.slot_volume_source === 'eval_2_0' &&
      detail.eval2EvaluationId === null
        ? 'eval_3_0'
        : ((detail.configurations.slot_volume_source as SlotVolumeSource) ??
          'eval_3_0'),
    slotAfeSource:
      (detail.configurations.cost_source as SlotAfeSource) ?? 'afe',
    priceDeckId: detail.configurations.price_deck_id ?? null,
    dcaScenarioId: detail.configurations.dca_scenario_id ?? null,
    slotVolumeConfigurations,
    slotAfeConfigurations,
    slotCostDateConfigurations,
    slotDateConfigurations,
    scenarioId: detail.scenario?.id ?? null,
    eval2Id: detail.eval2EvaluationId ?? null,
    dpsId: detail.configurations.development_pace_scenario_id
      ? Number(detail.configurations.development_pace_scenario_id)
      : null,
    drsId: detail.configurations.discount_rate_scenario_id
      ? Number(detail.configurations.discount_rate_scenario_id)
      : null,
    differentialScenarioId: detail.configurations.differential_scenario_id
      ? Number(detail.configurations.differential_scenario_id)
      : null,
    taxScenarioId: detail.configurations.tax_scenario_id
      ? Number(detail.configurations.tax_scenario_id)
      : null,
    expenseScenarioId: detail.configurations.expense_scenario_id
      ? Number(detail.configurations.expense_scenario_id)
      : null,
  };
};

// ---------------------------------------------------------------------------
// buildCalculateRequest
// ---------------------------------------------------------------------------

/**
 * Parameters for building a CalculateRequest payload.
 *
 * @property {number} workspaceId - The target workspace ID.
 * @property {number} eval2EvaluationId - The selected Eval 2.0 run ID.
 * @property {number} scenarioId - The scenario ID.
 * @property {number} packageId - The package ID.
 * @property {string} effectiveDate - The effective date.
 * @property {string} slotVolumeSource - Default volume source for slots.
 * @property {Record<string, SlotVolumeSourceConfiguration>} slotVolumeConfigurations - Per-slot volume overrides.
 */
export interface BuildCalculateRequestParams {
  workspaceId: number;
  // INS-8827: BE no longer accepts `eval2_evaluation_id`. The Scenario
  // picker is the single source of `scenario_id` (mandatory).
  scenarioId: number | null;
  packageId: number;
  effectiveDate: string;
  slotVolumeSource: string;
  slotAfeSource: string;
  priceDeckId: number | null;
  dcaScenarioId: number | null;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  slotDateConfigurations?: Record<string, SlotDateOverride>;
  // Evaluation Case mode — when set, individual scenario IDs are not sent
  evalMode?: 'case' | 'legacy';
  evaluationCaseId?: number | null;
  // DPS-specific fields (legacy mode only)
  dpsId?: number | null;
  slotsBlobPath?: string;
  scheduledSlotsBlobPath?: string;
  // DRS — resolved synchronously at eval start by Rails
  drsId?: number | null;
  // Differential Scenario — resolved synchronously at eval start by Rails
  differentialScenarioId?: number | null;
  // Tax Scenario — resolved synchronously at eval start by Rails
  taxScenarioId?: number | null;
  // Expense Scenario — resolved synchronously at eval start by Rails
  expenseScenarioId?: number | null;
}

/**
 * Builds a CalculateRequest payload from the current working copy state.
 * Converts camelCase per-slot overrides back to the snake_case API format.
 *
 * @param {BuildCalculateRequestParams} params - The current calculation parameters.
 * @return {CalculateRequest} The request body for POST /evaluations/calculate.
 */
/** One entry of the `slot_overrides` operations log, as the server stores it. */
export type SlotOverrideWireEntry = {
  type: 'individual';
  slot_id: string;
  overrides: Record<string, unknown>;
};

/**
 * Whether the run is a case run, as Calculate decides it (buildCalculateRequest): case mode with a
 * case selected. A case still held in legacy mode is not one.
 *
 * @param {object} config - The eval mode and the selected case.
 * @return {boolean} True for a case run.
 */
export const isCaseRun = (config: {
  evalMode?: string | null;
  evaluationCaseId?: number | null;
}): boolean => config.evalMode === 'case' && Boolean(config.evaluationCaseId);

/**
 * The DCA the slot tables and the DPS schedule read. A case run takes the case's own DCA (the
 * server stamps it, and buildCalculateRequest sends none), so a DCA picked before the case was
 * selected, its picker now hidden, can't drive them; null (Legacy) only while the case's detail
 * loads. A completed run of that case reads the DCA it ran with (`runDcaScenarioId`, its stamped
 * one), not the case's current one, which may have changed since. Otherwise the picked one.
 *
 * @param {object} config - The eval mode, the selected case and the picked DCA.
 * @param {object | null} caseDetail - The loaded case's detail (its id and DCA), if any.
 * @param {number | null} [runDcaScenarioId] - The DCA a completed run of the selected case ran
 *   with (null for Legacy); undefined when no such run is shown.
 * @return {number | null} The DCA to use.
 */
export const workingDcaScenarioId = (
  config: {
    evalMode?: string | null;
    evaluationCaseId?: number | null;
    dcaScenarioId?: number | null;
  },
  caseDetail?: { id: number; dcaScenarioId?: number | null } | null,
  runDcaScenarioId?: number | null
): number | null => {
  if (isCaseRun(config)) {
    if (runDcaScenarioId !== undefined) return runDcaScenarioId;
    return caseDetail && caseDetail.id === config.evaluationCaseId
      ? (caseDetail.dcaScenarioId ?? null)
      : null;
  }
  return config.dcaScenarioId ?? null;
};

export const buildCalculateRequest = (
  params: BuildCalculateRequestParams
): CalculateRequest => {
  const isEval3 = params.slotVolumeSource === 'eval_3_0';

  const hasVolumeOverrides =
    isEval3 && Object.keys(params.slotVolumeConfigurations).length > 0;

  const slotVolumeSources = hasVolumeOverrides
    ? Object.entries(params.slotVolumeConfigurations).map(
        ([slotId, config]) => ({
          slot_id: slotId,
          volume_source_id: config.volumeSourceId,
          volume_source_type: config.volumeSourceType,
          is_user_modified: true,
          ...(config.forecastSource
            ? { forecast_source: config.forecastSource }
            : {}),
          ...(config.dcaScenario ? { dca_scenario: config.dcaScenario } : {}),
        })
      )
    : [];

  // Merge AFE source + cost date configs into slot_afe_sources (one entry per slot)
  const afeSlotIds = new Set([
    ...Object.keys(params.slotAfeConfigurations),
    ...Object.keys(params.slotCostDateConfigurations),
  ]);

  // Serialize slot date overrides (permit_date, spud_date, etc.)
  const slotDateOverrides =
    params.slotDateConfigurations &&
    Object.keys(params.slotDateConfigurations).length > 0
      ? Object.entries(params.slotDateConfigurations)
          .filter(([, v]) => v !== undefined)
          .map(([slotId, dates]) => ({
            slot_id: slotId,
            permit_date: dates.permitDate,
            spud_date: dates.spudDate,
            first_production_date: dates.firstProductionDate,
            cashflow_date: dates.cashflowDate,
          }))
      : [];

  const isAfeMode = params.slotAfeSource === 'afe';
  const hasAfeOverrides = isAfeMode && afeSlotIds.size > 0;

  const slotAfeSources = hasAfeOverrides
    ? Array.from(afeSlotIds).map((slotId) => {
        const afeConfig = params.slotAfeConfigurations[slotId];
        const costDateConfig = params.slotCostDateConfigurations[slotId];

        return {
          slot_id: slotId,
          ...(afeConfig?.afeSourceId
            ? { afe_header_id: afeConfig.afeSourceId }
            : {}),
          ...(costDateConfig?.drillingCostsDate
            ? { drilling_costs_date: costDateConfig.drillingCostsDate }
            : {}),
          ...(costDateConfig?.completionCostsDate
            ? { completion_costs_date: costDateConfig.completionCostsDate }
            : {}),
          ...(costDateConfig?.facilitiesCostsDate
            ? { facilities_costs_date: costDateConfig.facilitiesCostsDate }
            : {}),
          ...(costDateConfig?.contingenciesCostsDate
            ? {
                contingencies_costs_date: costDateConfig.contingenciesCostsDate,
              }
            : {}),
          ...(costDateConfig?.otherCostsDate
            ? { other_costs_date: costDateConfig.otherCostsDate }
            : {}),
        };
      })
    : [];

  return {
    workspace_id: params.workspaceId,
    scenario_id: params.scenarioId,
    package_id: params.packageId,
    configurations: {
      effective_date: params.effectiveDate,
      slot_volume_source: params.slotVolumeSource,
      cost_source: params.slotAfeSource,
      // Price Deck: skip in case mode (read from the EvaluationCase on backend)
      ...(params.evalMode !== 'case' && params.priceDeckId
        ? { price_deck_id: params.priceDeckId }
        : {}),
      // DCA: skip on a case run — the server stamps the case's DCA on the run.
      ...(params.dcaScenarioId && !isCaseRun(params)
        ? { dca_scenario_id: params.dcaScenarioId }
        : {}),
      ...(slotVolumeSources.length > 0
        ? { slot_volume_sources: slotVolumeSources }
        : {}),
      ...(slotAfeSources.length > 0
        ? { slot_afe_sources: slotAfeSources }
        : {}),
      ...(slotDateOverrides.length > 0
        ? { slot_date_overrides: slotDateOverrides }
        : {}),
      // Evaluation Case mode: send evaluation_case_id, skip individual scenario IDs.
      // CaseResolver handles DPS/Tax/DRS/Differential resolution from the case.
      ...(isCaseRun(params)
        ? { evaluation_case_id: params.evaluationCaseId }
        : {
            // Legacy mode: individual scenario IDs
            // DPS blob paths — included when calculating via DPS instead of Eval 2.0
            ...(params.dpsId
              ? {
                  development_pace_scenario_id: params.dpsId,
                  slots_blob_path: params.slotsBlobPath,
                  scheduled_slots_blob_path: params.scheduledSlotsBlobPath,
                }
              : {}),
            // DRS — resolved synchronously at eval start by Rails
            ...(params.drsId
              ? { discount_rate_scenario_id: params.drsId }
              : {}),
            // Differential Scenario — resolved synchronously at eval start by Rails
            ...(params.differentialScenarioId
              ? { differential_scenario_id: params.differentialScenarioId }
              : {}),
            ...(params.taxScenarioId
              ? { tax_scenario_id: params.taxScenarioId }
              : {}),
            // Expense Scenario — resolved synchronously at eval start by Rails
            ...(params.expenseScenarioId
              ? { expense_scenario_id: params.expenseScenarioId }
              : {}),
          }),
    },
  };
};

// ---------------------------------------------------------------------------
// convertToAggregatedFreeCashFlow
// ---------------------------------------------------------------------------

/**
 * Transforms the raw API response into the AggregatedFreeCashFlow shape
 * consumed by chart and table components. Handles date extraction, cash flow
 * zipping, and production volume aggregation.
 *
 * @param {object} result - The non-null result from AggregatedFreeCashFlowResultResponse.
 * @param {object} request - The non-null request from AggregatedFreeCashFlowResultResponse.
 * @return {AggregatedFreeCashFlow} The transformed data ready for rendering.
 */
export const convertToAggregatedFreeCashFlow = (
  result: Exclude<
    AggregatedFreeCashFlowResultResponse['results'][number]['result'],
    null
  >,
  request: Exclude<
    AggregatedFreeCashFlowResultResponse['results'][number]['request'],
    null
  >
): AggregatedFreeCashFlow => {
  let dates: string[];
  let interval: FreeCashFlowAggregationPeriod;

  if (result.year) {
    interval = 'year';
    dates = result.year;
  } else if (result.quarter) {
    interval = 'quarter';
    dates = result.quarter;
  } else if (result.month) {
    interval = 'month';
    dates = result.month;
  } else {
    dates = [];
    interval = 'year';
  }

  const freeCashflows = zip<string | number | SlotCategory>(
    dates,
    result.grossCashflow,
    result.netCashflow,
    result.slotCategory,
    result.cumulativeGrossCashflow,
    result.cumulativeNetCashflow
  )
    .map(
      ([
        date,
        grossCashflow,
        netCashflow,
        category,
        cumulativeGrossCashflow,
        cumulativeNetCashflow,
      ]) => {
        if (
          !date ||
          grossCashflow === undefined ||
          netCashflow === undefined ||
          cumulativeGrossCashflow === undefined ||
          cumulativeNetCashflow === undefined ||
          !category
        )
          return null;
        return {
          date,
          grossCashflow,
          netCashflow,
          category,
          cumulativeGrossCashflow,
          cumulativeNetCashflow,
        };
      }
    )
    .filter((entry): entry is FreeCashFlowEntry => entry !== null);

  let productionVolumes: Array<{
    date: string;
    category: ProductionType;
    type: string;
    volume: number;
  }> = [];

  if (
    dates &&
    [
      'grossOil',
      'netOil',
      'grossGasSold',
      'netGasSold',
      'grossNgl',
      'netNgl',
      'grossWater',
      'netWater',
      'totalCumulativeGrossOil',
      'totalCumulativeNetOil',
      'totalCumulativeGrossGasSold',
      'totalCumulativeNetGasSold',
      'totalCumulativeGrossNgl',
      'totalCumulativeNetNgl',
      'totalCumulativeGrossWater',
      'totalCumulativeNetWater',
    ].every((key) => Array.isArray((result as any)[key]))
  ) {
    const volumeMap: Array<{
      type: ProductionVolumeEntry['type'];
      category: ProductionType;
    }> = [
      { type: 'grossOil', category: 'oil' },
      { type: 'netOil', category: 'oil' },
      { type: 'grossGasSold', category: 'gas' },
      { type: 'netGasSold', category: 'gas' },
      { type: 'grossNgl', category: 'ngl' },
      { type: 'netNgl', category: 'ngl' },
      { type: 'grossWater', category: 'water' },
      { type: 'netWater', category: 'water' },
      { type: 'totalCumulativeGrossOil', category: 'cumulativeOil' },
      { type: 'totalCumulativeNetOil', category: 'cumulativeOil' },
      { type: 'totalCumulativeGrossGasSold', category: 'cumulativeGas' },
      { type: 'totalCumulativeNetGasSold', category: 'cumulativeGas' },
      { type: 'totalCumulativeGrossNgl', category: 'cumulativeNgl' },
      { type: 'totalCumulativeNetNgl', category: 'cumulativeNgl' },
      { type: 'totalCumulativeGrossWater', category: 'cumulativeWater' },
      { type: 'totalCumulativeNetWater', category: 'cumulativeWater' },
    ];

    const rawVolumes = dates.flatMap((date, index) =>
      volumeMap.map(({ type, category }) => ({
        date,
        category,
        type,
        volume:
          typeof type === 'string' ? (result as any)[type]?.[index] || 0 : 0,
      }))
    );

    // NOTE: the eval3-new revamp keeps a hand-synced replica of this collapse in
    // `pages/packages/show/eval3-new/utils.tsx` (`toAggregatedProductionVolumes`) — it does
    // NOT import this legacy code. If you change the grouping/sum semantics here, update the
    // replica too (they drifted once, causing a Production Volumes under-count).
    const grouped = groupBy(
      rawVolumes,
      (v) => `${v.date}-${v.category}-${v.type}`
    );

    productionVolumes = Object.values(grouped).map((group) => ({
      date: group[0]?.type?.startsWith('totalCumulative')
        ? (group.at(-1)?.date ?? '')
        : group[0].date,
      category: group[0].category,
      type: group[0].type as string,
      volume: group[0]?.type?.startsWith('totalCumulative')
        ? (group.at(-1)?.volume ?? 0)
        : group.reduce((sum, entry) => sum + entry.volume, 0),
    }));
  }

  return {
    packageOverview: result.packageOverview,
    freeCashflows,
    interval,
    effectiveDate:
      request.appliedEffectiveDate ?? request.effectiveDateOverride,
    productionVolumes: productionVolumes as ProductionVolumeEntry[],
    rescatSlotCount: result.rescatSlotCount,
    slotVolumeSource: request.configuration?.slotVolumeSource ?? 'eval_3_0',
    slotVolumeSources: request.configuration?.slotVolumeSources,
  };
};

// ---------------------------------------------------------------------------
// backfillAfeConfigurations
// ---------------------------------------------------------------------------

/**
 * Fetches missing AFE data (costs + wellName) for slot AFE overrides that
 * only have an afeSourceId (as restored from a saved calculation).
 * Makes a single POST /queries/afe_summaries call with all unique AFE IDs,
 * then enriches each slot config with the returned costs and wellName.
 *
 * @param {Record<string, SlotAfeSourceConfiguration>} configs - Current slot AFE configs (may lack costs/wellName).
 * @return {Promise<Record<string, SlotAfeSourceConfiguration>>} Enriched configs with costs and wellName backfilled.
 */
export const backfillAfeConfigurations = async (
  configs: Record<string, SlotAfeSourceConfiguration>
): Promise<Record<string, SlotAfeSourceConfiguration>> => {
  const slotsNeedingBackfill = Object.entries(configs).filter(
    ([, config]) => config.afeSourceId && config.drillingCost === undefined
  );

  if (slotsNeedingBackfill.length === 0) return configs;

  const uniqueAfeIds = [
    ...new Set(slotsNeedingBackfill.map(([, config]) => config.afeSourceId)),
  ];

  try {
    const response = await httpRequest.post<AfeSummariesResponse, unknown>(
      '/queries/afe_summaries',
      { ids: uniqueAfeIds },
      new AbortController()
    );
    if (response.type !== 'success') return configs;

    const summaryById = new Map(
      response.data.afeSummaries.map((s) => [String(s.id), s])
    );

    const enriched = { ...configs };
    for (const [slotId, config] of slotsNeedingBackfill) {
      const summary = summaryById.get(String(config.afeSourceId));
      if (!summary) continue;

      enriched[slotId] = {
        ...config,
        afeWellName: summary.wellName ?? undefined,
        drillingCost: summary.drillingCost,
        completionCost: summary.completionCost,
        facilitiesCost: summary.facilitiesCost,
        contingenciesCost: summary.contingenciesCost,
        otherCost: summary.otherCost,
      };
    }

    return enriched;
  } catch {
    return configs;
  }
};

/**
 * This page's unsaved edits, as a DIFF against the run it loaded.
 *
 * A diff, not a dump. The working maps are HYDRATED from the saved calculation
 * (`extractWorkingCopyFromDetail`), so serializing them wholesale told the agent that a
 * freshly opened, untouched run with 30 saved overrides had 30 unsaved edits — every read
 * then carried a warning about pending state that did not exist.
 *
 * The reverse was worse. Removing an entry from a working map is how a user CLEARS a saved
 * override, and a dump emits nothing for an absent entry — so the clear had no tombstone,
 * the agent went on treating the cleared value as effective, and a later
 * `current_afe_header_id` selection could pick exactly the properties the user had just
 * taken off that AFE. An explicit `null` is the tombstone: `SlotOverrideResolver#merge_overrides`
 * assigns whatever value an operation carries, `nil` included, so a null already means
 * "no longer overridden" to the calculator.
 *
 * Edits the run will DISCARD are still reported, deliberately. The obvious move is to
 * filter them out — the calculator drops per-well AFEs unless the run prices from AFEs,
 * and per-well forecasts unless it takes Eval 3.0 volumes — but silence there leaves the
 * user with a screen full of edits that quietly do nothing when they press Calculate. The
 * server knows the run's modes and classifies them, so the agent can say WHY they will not
 * apply rather than either asserting them or ignoring them.
 *
 * @param {object} params - Working maps, the loaded run to diff against, and the run modes.
 * @return {SlotOverrideWireEntry[]} One entry per CHANGED slot, empty when nothing differs.
 */
export const buildPendingOperations = (params: {
  baseline: CalculationDetail | null;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  slotDateConfigurations?: Record<string, SlotDateOverride>;
}): SlotOverrideWireEntry[] => {
  // The baseline goes through the SAME extractor that hydrated the working maps, so the
  // two sides are directly comparable and cannot drift into a false difference.
  const base = params.baseline
    ? extractWorkingCopyFromDetail(params.baseline)
    : {
        slotVolumeConfigurations: {},
        slotAfeConfigurations: {},
        slotCostDateConfigurations: {},
        slotDateConfigurations: {},
      };

  const changes: Record<string, Record<string, unknown>> = {};

  /**
   * Records one field when it differs, using `null` to mean cleared.
   *
   * @param {string} slotId - The slot the field belongs to.
   * @param {string} field - The wire field name.
   * @param {unknown} now - The working value, if any.
   * @param {unknown} was - The saved value, if any.
   * @return {void}
   */
  const diffField = (
    slotId: string,
    field: string,
    now: unknown,
    was: unknown
  ) => {
    const nowVal = now ?? null;
    const wasVal = was ?? null;
    if (nowVal === wasVal) return;
    changes[slotId] = { ...(changes[slotId] ?? {}), [field]: nowVal };
  };

  /**
   * Diffs one group of per-slot fields across the union of both sides' slot ids.
   *
   * @param {object} working - The working map for this group.
   * @param {object} saved - The saved map for this group.
   * @param {object} fields - Wire field name to the reader that pulls it off an entry.
   * @return {void}
   */
  const diffGroup = <T>(
    working: Record<string, T> | undefined,
    saved: Record<string, T> | undefined,
    fields: Record<string, (entry: T | undefined) => unknown>
  ) => {
    const ids = new Set([
      ...Object.keys(working ?? {}),
      ...Object.keys(saved ?? {}),
    ]);
    ids.forEach((slotId) => {
      Object.entries(fields).forEach(([field, read]) => {
        diffField(
          slotId,
          field,
          read(working?.[slotId]),
          read(saved?.[slotId])
        );
      });
    });
  };

  diffGroup<SlotVolumeSourceConfiguration>(
    params.slotVolumeConfigurations,
    base.slotVolumeConfigurations,
    {
      volume_source_id: (e) => e?.volumeSourceId,
      volume_source_type: (e) => e?.volumeSourceType,
      forecast_source: (e) => e?.forecastSource,
      dca_scenario: (e) => e?.dcaScenario,
    }
  );

  // The AFE and its cost dates move together.
  diffGroup<SlotAfeSourceConfiguration>(
    params.slotAfeConfigurations,
    base.slotAfeConfigurations,
    { afe_header_id: (e) => e?.afeSourceId }
  );
  diffGroup<SlotCostDateConfiguration>(
    params.slotCostDateConfigurations,
    base.slotCostDateConfigurations,
    {
      drilling_costs_date: (e) => e?.drillingCostsDate,
      completion_costs_date: (e) => e?.completionCostsDate,
      facilities_costs_date: (e) => e?.facilitiesCostsDate,
      contingencies_costs_date: (e) => e?.contingenciesCostsDate,
      other_costs_date: (e) => e?.otherCostsDate,
    }
  );

  // Schedule dates are sent whatever the cost and volume modes are, as Calculate does.
  diffGroup<SlotDateOverride>(
    params.slotDateConfigurations,
    base.slotDateConfigurations,
    {
      permit_date: (e) => e?.permitDate,
      spud_date: (e) => e?.spudDate,
      first_production_date: (e) => e?.firstProductionDate,
      cashflow_date: (e) => e?.cashflowDate,
    }
  );

  return Object.entries(changes).map(([slotId, overrides]) => ({
    type: 'individual' as const,
    slot_id: slotId,
    overrides,
  }));
};

/**
 * Run-level settings this page is holding that differ from the run it loaded.
 *
 * A diff for the same reason the overrides are: these are hydrated from the saved
 * calculation, so reporting them wholesale would tell the agent every untouched run had
 * three pending settings changes. `null` means cleared, as it does for an override.
 *
 * @param {object} params - The working settings and the run they were loaded from.
 * @return {object} Only the settings that differ, empty when none do.
 */
export const buildPendingSettings = (params: {
  baseline: CalculationDetail | null;
  evalMode?: string | null;
  scenarioId?: number | null;
  evaluationCaseId?: number | null;
  dcaScenarioId?: number | null;
  costSource?: string | null;
  volumeSource?: string | null;
  effectiveDate?: string | null;
  priceDeckId?: number | null;
}): Record<string, number | string | null> => {
  const configs = params.baseline?.configurations;
  // A case run takes its DCA from the case and Calculate sends none, so a DCA picked before the
  // case (its picker now hidden) is no pending change. A case run as Calculate decides it.
  const caseLevel = isCaseRun(params);

  return diffPendingSettings(
    {
      scenarioId: params.scenarioId,
      evaluationCaseId: params.evaluationCaseId,
      ...(caseLevel ? {} : { dcaScenarioId: params.dcaScenarioId }),
      costSource: params.costSource,
      volumeSource: params.volumeSource,
      effectiveDate: params.effectiveDate,
      priceDeckId: params.priceDeckId,
    },
    {
      // `scenario_id` is a column on the calculation, not part of `configurations`.
      scenarioId: params.baseline?.scenario?.id,
      evaluationCaseId: configs?.evaluation_case_id,
      ...(caseLevel ? {} : { dcaScenarioId: configs?.dca_scenario_id }),
      costSource: configs?.cost_source,
      volumeSource: configs?.slot_volume_source,
      effectiveDate: configs?.effective_date,
      priceDeckId: configs?.price_deck_id,
    }
  );
};

/**
 * The scheduled-slots blob a restored run's grid and slot exports read: the schedule the run priced
 * when the detail names one (a legacy run that dropped replaced LTD places priced a rebuilt schedule,
 * INS-13303), else the snapshot's. scheduledSlotsSasUrl signs the same blob.
 * @param {CalculationDetail} detail - The calculation detail response
 * @return {string} The blob path
 */
export const restoredScheduledSlotsBlobPath = (
  detail: CalculationDetail
): string =>
  detail.pricedScheduledSlotsBlobPath ??
  detail.configurations.scheduled_slots_blob_path ??
  '';

/**
 * Whether the signed-in user created the workspace, matched by name as the Mine
 * view does. A service account creator has a null id (its id is a user's id
 * too), so it never matches, whatever its name; every other case reads as before.
 *
 * @param {EvaluationWorkspace['createdBy']} createdBy - The workspace's creator.
 * @param {string | undefined} currentUserName - The signed-in user's name.
 * @return {boolean} True when the creator is the signed-in user.
 */
export const isCreatedByCurrentUser = (
  createdBy: EvaluationWorkspace['createdBy'],
  currentUserName: string | undefined
): boolean => createdBy?.id !== null && createdBy?.name === currentUserName;
